import { isValidLanguage } from '@data/types/common';
const BaseRoutes = {
    Property: '/reservation/:propertyCode',
    PropertyBr: '/reservas/:propertyCode',
};
/**
 * The standalone legal-policy pages, mounted in Property.tsx as static leaf
 * segments of the property namespace. Only the leaf is listed: the aliases
 * (`/reservation`, `/reservas`) and the optional `/:lang` prefix are matched by
 * the Property routes above. Nesting them there also means the existing MFD
 * server routing serves them with no new top-level route.
 */
export const LEGAL_POLICY_SECTIONS = [
    'terms',
    'privacy',
];
/**
 * Builds the path to a standalone legal-policy page. Nested under the property
 * (reservation) namespace so it's served by existing routing. Includes the
 * language segment when provided so the page loads in the guest's current
 * language; omitting it is safe (the page falls back to the property default).
 */
export const buildStandalonePropertyPath = (propertyCode, section, lang) => {
    const base = `/reservation/${propertyCode}/${section}`;
    return lang ? `/${lang}${base}` : base;
};
/**
 * The language the path itself carries, if any — `/:lang` is optional on every
 * route, so a missing segment is a normal case, not an error.
 */
export const getLanguageSegment = (pathname) => {
    const [first] = pathname.split('/').filter(Boolean);
    return first && isValidLanguage(first) ? first : undefined;
};
const PROPERTY_NAMESPACES = [
    BaseRoutes.Property,
    BaseRoutes.PropertyBr,
].map((namespace) => namespace.split('/')[1]);
/**
 * Rewrites — or inserts — the leading `/:lang` segment of a path so the URL
 * reflects the language the guest is actually reading. Pure string work: the
 * caller decides how to apply it (both the booking flow and the standalone
 * legal pages use a history replace, so no reload and no extra history entry).
 *
 * The slot is positional, not validated: `/:lang` matches any segment, so
 * `/foo/reservation/…` reaches these routes and must be *replaced*. Prefixing
 * it would push the path out of every route and 404 the guest.
 */
export const withLanguageSegment = (pathname, language) => {
    const parts = pathname.split('/').filter(Boolean);
    const carriesLanguageSlot = parts.length > 0 &&
        (isValidLanguage(parts[0]) || PROPERTY_NAMESPACES.includes(parts[1]));
    if (carriesLanguageSlot) {
        parts[0] = language;
    }
    else {
        parts.unshift(language);
    }
    return `/${parts.join('/')}`;
};
export const SubRoutes = {
    Accommodations: '',
    Addons: 'addons',
    Confirmation: 'confirmation',
    ExternalPaymentPage: 'payment-processor',
    Guests: 'guests',
    Payment: 'payment',
};
export const Routes = Object.assign(Object.assign({}, BaseRoutes), SubRoutes);
export const getPageFromRoute = (route) => {
    switch (route) {
        case Routes.Accommodations:
            return 'Accommodations';
        case Routes.Addons:
            return 'Addons';
        case Routes.Confirmation:
            return 'Confirmation';
        case Routes.Guests:
            return 'Guests';
        case Routes.Payment:
            return 'Payment';
        default:
            return 'Landing';
    }
};
export function getPropertyRoot(pathname, includeOrigin = false) {
    let route = includeOrigin ? window.location.origin : '';
    const parts = pathname.split('/');
    if (parts.length < 2)
        return route;
    let reservationsIndex = parts.indexOf(Routes.Property.substring(1).split('/')[0]);
    if (reservationsIndex === -1) {
        reservationsIndex = parts.indexOf(Routes.PropertyBr.substring(1).split('/')[0]);
    }
    if (reservationsIndex !== -1 && parts.length > reservationsIndex + 1) {
        route = parts.splice(0, reservationsIndex + 2).join('/');
    }
    return route;
}
